import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchEasyModeRecords } from '../services/easyModeService';
import {
  buildRecordByJoinKey,
  getIsRecordAwaitingClassification,
  getRecordForUniqueKey,
  mergeRecordByS3UrlPath
} from '../utils/easyModeRecords';
import usePolling from './usePolling';

// the poll is how classification reaches this page; 15s because the user is watching it rather than
// the case list
export const EASY_MODE_RECORD_POLL_INTERVAL_MS = 15000;

const useEasyModeRecords = ({ caseId, awaitedS3UrlPaths = [] }) => {
  const [recordByS3UrlPath, setRecordByS3UrlPath] = useState({});
  const [isRecordsLoading, setIsRecordsLoading] = useState(true);

  const refetchRecords = useCallback(async () => {
    if (!caseId) return;

    try {
      const records = await fetchEasyModeRecords({ caseId });

      setRecordByS3UrlPath((previous) => mergeRecordByS3UrlPath({ previous, records }));
    } catch (error) {
      // a missed poll is recoverable on the next tick, so it must not empty the list
      console.error('Error fetching easy mode records:', error);
    }
  }, [caseId]);

  useEffect(() => {
    let isStale = false;
    setIsRecordsLoading(true);

    refetchRecords().finally(() => {
      if (!isStale) setIsRecordsLoading(false);
    });

    return () => {
      isStale = true;
    };
  }, [refetchRecords]);

  // a fetch only ever merges records in, so a removed one has to be dropped by hand or the next
  // reseed brings it back
  const dropRecord = useCallback(({ s3UrlPath }) => {
    setRecordByS3UrlPath((previous) => {
      if (!previous[s3UrlPath]) return previous;

      const { [s3UrlPath]: _removed, ...rest } = previous;

      return rest;
    });
  }, []);

  const recordByJoinKey = useMemo(
    () => buildRecordByJoinKey({ recordByS3UrlPath }),
    [recordByS3UrlPath]
  );

  // an uploaded file with no record yet is still awaited, because the PFQ creates it a moment later
  const hasAwaitedClassification = awaitedS3UrlPaths.some((s3UrlPath) =>
    getIsRecordAwaitingClassification(getRecordForUniqueKey({ recordByJoinKey, uniqueKey: s3UrlPath }))
  );

  usePolling({
    intervalMs: EASY_MODE_RECORD_POLL_INTERVAL_MS,
    callback: refetchRecords,
    enabled: hasAwaitedClassification
  });

  return {
    recordByS3UrlPath,
    recordByJoinKey,
    recordCount: Object.keys(recordByS3UrlPath).length,
    isRecordsLoading,
    refetchRecords,
    dropRecord
  };
};

export default useEasyModeRecords;
